<template>
  <div id="financial-transactions" class="align-start fill-height">
    <!-- mobile version -->
    <template v-if="$vuetifyDisplay.smAndDown">
      <v-card class="mt-4 px-8 py-6">
        <div class="text-center">
          To use this feature you must access your portal from a Desktop device
        </div>
        <div class="text-center">
          <v-btn
            rounded
            class="i-btn-block-secondary mt-4"
            :to="{ name: 'dashboard' }"
          >
            Dashboard
          </v-btn>
        </div>
      </v-card>
    </template>

    <!-- desktop version -->
    <template v-else>
      <v-row class="g-container-large" no-gutters>
        <v-col cols="12">
          <h2>{{ $route.meta.title }}</h2>

          <v-row>
            <v-spacer></v-spacer>
            <v-col cols="12" sm="4" md="3" lg="2">
              <i-instructions
                instructionKey="reports/transactions"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active':
                    selectedPaymentType === 'PAYMENT_SERVICES'
                }"
                :disabled="!canAccessPaymentServices"
                @click="selectPaymentType('PAYMENT_SERVICES')"
              >
                Payment Services
              </v-btn>
            </v-col>
            <v-col
              v-if="featureCreditCardPaymentEnabled"
              cols="6"
              md="3"
              lg="2"
            >
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': selectedPaymentType === 'CREDIT_CARD'
                }"
                :disabled="!canAccessCardServices"
                @click="selectPaymentType('CREDIT_CARD')"
              >
                Card Services
              </v-btn>
            </v-col>
          </v-row>

          <i-search-sort
            dateRangeColumnName="Created Date"
            initialDateRange="lastSevenDays"
            :executeOnMount="true"
            :loading="fetching"
            :routeName="$route.name"
            exportButtonText="Export"
            :showExportButton="true"
            :disableExportButton="false"
            :maxExportableItems="-1"
            :resultsQuantity="transactions.length"
            :fetchingExportFile="fetchingExcel"
            @searchQuery="searchQuery"
            @fetchExportFile="fetchExcel"
          />

          <i-progress-linear v-if="fetching && $vuetifyDisplay.smAndDown" />

          <template v-if="!fetching && transactions.length === 0">
            <i-empty-results
              buttonText="Back to Reports"
              buttonRouteName="reports"
              :title="
                neverFetched
                  ? 'Define search criteria and press Search to generate the report'
                  : 'No Transactions found'
              "
            />
          </template>

          <template v-else>
            <v-data-table
              class="pt-2 elevation-1 i-data-table"
              :headers="headers"
              :items="transactions"
              :loading="fetching"
              :items-per-page="-1"
              hide-default-footer
            >
              <template #[`item.referenceNumber`]="{ item }">
                <router-link
                  v-if="createLinkForTransaction(item)"
                  class="i-a-link"
                  :to="createLinkForTransaction(item)"
                >
                  <template>{{ $filters.text(item.referenceNumber) }}</template>
                </router-link>
                <span v-else>{{ $filters.text(item.referenceNumber) }}</span>
              </template>

              <template v-slot:[`item.store.name`]="{ item }">
                <span v-if="item.store">{{ item.store.name }}</span>
                <span v-else>{{ item.storeName }}</span>
              </template>

              <template v-slot:[`item.objectCreatedDate`]="{ item }">
                <span>{{
                  $filters.formatDateTime12h(item.objectCreatedDate)
                }}</span>
              </template>

              <template v-slot:[`item.type`]="{ item }">
                <span>{{ mapToDisplayType(item.type) }}</span>
              </template>

              <template v-slot:[`item.storeOrderReference`]="{ item }">
                <span>{{ item.storeOrderReference }}</span>
              </template>

              <template v-slot:[`item.supplierOrderNumber`]="{ item }">
                <span>{{ item.supplierOrderNumber }}</span>
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <span
                  :class="{
                    'highlight-status-text-color':
                      item.status === 'CANCELLED' ||
                      item.status === 'REJECTED' ||
                      item.status === 'OUTSTANDING' ||
                      item.status === 'IN_PROCESS' ||
                      item.status === 'PROCESSED_OFFLINE'
                  }"
                >
                  {{ mapToDisplayStatus(item.status) }}
                </span>
              </template>

              <template v-slot:[`item.paymentType`]="{ item }">
                <i-payment-type
                  v-if="item.paymentType"
                  :paymentType="item.paymentType"
                  :textOnly="true"
                />
              </template>

              <template v-slot:[`item.finalisedDate`]="{ item }">
                <span>{{
                  $filters.formatDateTime12h(item.finalisedDate)
                }}</span>
                <span v-if="item.finalisedDate">
                  <v-icon
                    small
                    v-tooltip="{
                      html: `transactionFinalisedDateToolTipContent-${item.objectId}`
                    }"
                  >
                    mdi-information
                  </v-icon>
                  <span
                    :id="`transactionFinalisedDateToolTipContent-${item.objectId}`"
                  >
                    <span
                      v-if="
                        item.type === 'SUPPLIER_ORDER' &&
                        tenantPaymentTransactionsConfig.purchaseOrderFinalisedDateField ===
                          'SUPPLIER_DELIVERED_ENTRY_DATE'
                      "
                      >Date Supplier added Delivered to Order</span
                    >
                    <span v-else-if="item.type === 'SUPPLIER_ORDER'"
                      >Date when the retailer accepted the order</span
                    >
                    <span v-else-if="item.type === 'SUPPLIER_ORDER_QUERY'"
                      >Date when the order query was resolved</span
                    >
                    <span v-else-if="item.type === 'CREDIT_RETURN'"
                      >Date when the credit return was resolved</span
                    >
                    <span v-else-if="item.type === 'PAYMENT_ADJUSTMENT'"
                      >Date when the payment adjustment was created</span
                    >
                  </span>
                </span>
              </template>

              <template v-slot:[`item.storeOrderDate`]="{ item }">
                <span>{{
                  $filters.formatDateTime12h(item.storeOrderDate)
                }}</span>
              </template>

              <template
                v-slot:[`item.tenantOwnerSupplierChargeIncTax`]="{ item }"
              >
                <i-currency-display
                  :dollarValue="item.tenantOwnerSupplierChargeIncTax"
                  :strong="false"
                ></i-currency-display>
              </template>

              <template v-slot:[`item.supplierPayoutTotalIncTax`]="{ item }">
                <i-currency-display
                  :dollarValue="item.supplierPayoutTotalIncTax"
                  :strong="false"
                ></i-currency-display>
              </template>

              <template v-slot:[`item.transactionPeriod`]="{ item }">
                <span v-if="item.transactionPeriodNotApplicable"> N/A </span>
                <template v-else-if="item.transactionPeriod">
                  <router-link :to="paymentTransactionsRouteTo(item)">
                    {{ item.transactionPeriod.identifier }}
                  </router-link>
                  <v-icon
                    small
                    v-tooltip="{
                      html: `transactionPeriodToolTipContent-${item.objectId}`
                    }"
                    >mdi-information</v-icon
                  >
                  <span
                    :id="`transactionPeriodToolTipContent-${item.objectId}`"
                  >
                    Start Date:
                    {{
                      $filters.formatDateTime12h(
                        item.transactionPeriod.startDate
                      )
                    }}<br />
                    End Date:
                    {{
                      $filters.formatDateTime12h(
                        item.transactionPeriod.endDate
                      )
                    }}<br />
                  </span>
                </template>
              </template>

              <template v-slot:[`item.supplierPayoutDate`]="{ item }">
                <template v-if="item.payoutDisplayDate">
                  <div style="line-height: 1.1">
                    <div>{{ $filters.formatDate(item.payoutDisplayDate) }}</div>
                    <div class="text-caption">
                      {{ item.payoutDisplayStatus }}
                    </div>
                  </div>
                </template>
                <template v-else> N/A </template>
              </template>

              <template v-slot:[`body.append`]="{}">
                <tr v-if="!fetching">
                  <td colspan="7" class="pl-16">GRAND TOTAL</td>
                  <td>{{ $filters.currency(transactionsGrandTotal) }}</td>
                  <td colspan="5">&nbsp;</td>
                </tr>
              </template>
            </v-data-table>
          </template>
        </v-col>
      </v-row>
      <v-dialog
        v-if="confirmationDialog"
        v-model="confirmationDialog"
        width="400px"
        @click:outside="confirmationDialog = false"
        @keydown.esc="confirmationDialog = false"
      >
        <i-confirmation-dialog
          action-text="Search"
          :submitting="fetching"
          text="You have not defined any search criteria! This is not recommended. Do you want to search anyway?"
          @cancel="confirmationDialog = false"
          @confirm="doSearch"
        />
      </v-dialog>
    </template>
  </div>
</template>

<script>
import { COMMON_CONSTANTS } from '@/constants/Common.constants'
import { mapActions, mapGetters } from 'vuex'
import iPaymentType from '@/components/PaymentType'
import iProgressLinear from '@/components/ProgressLinear'
import iSearchSort from '@/components/SearchSort'
import {
  fetchTransactions,
  fetchTransactionsExcel
} from '@/services/transactions'
import moment from 'moment-timezone'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import { replacePaymentPeriodHeader } from '@/utils/common'
import iCurrencyDisplay from '@/components/CurrencyDisplay.vue'

export default {
  components: {
    iCurrencyDisplay,
    iEmptyResults,
    iInstructions,
    iPaymentType,
    iProgressLinear,
    iSearchSort,
    iConfirmationDialog
  },

  data: () => ({
    COMMON_CONSTANTS,
    fetching: false,
    fetchingExcel: false,
    neverFetched: true,
    transactions: [],
    confirmationDialog: false,
    queryObject: {},
    selectedPaymentType: 'PAYMENT_SERVICES'
  }),

  computed: {
    ...mapGetters('profile', [
      'bankDetailsAvailable',
      'processesOrdersOffline'
    ]),
    ...mapGetters('tenant', [
      'featureBankAccountsEnabled',
      'featureCreditCardPaymentEnabled',
      'featurePaymentServicesPaymentEnabled',
      'tenantPaymentTransactionsConfig',
      'isILR'
    ]),

    canAccessPaymentServices() {
      return (
        this.featurePaymentServicesPaymentEnabled &&
        !this.processesOrdersOffline
      )
    },

    canAccessCardServices() {
      return (
        this.featureCreditCardPaymentEnabled &&
        !this.processesOrdersOffline &&
        (!this.featureBankAccountsEnabled || this.bankDetailsAvailable)
      )
    },

    headers() {
      return [
        {
          title: 'CREATED',
          key: 'objectCreatedDate',
          text: 'CREATED',
          value: 'objectCreatedDate',
          width: '120px'
        },
        { title: 'TYPE', key: 'type', text: 'TYPE', value: 'type' },
        {
          title: 'STATUS',
          key: 'status',
          text: 'STATUS',
          value: 'status',
          width: '100px'
        },
        {
          title: 'REFERENCE',
          key: 'referenceNumber',
          text: 'REFERENCE',
          value: 'referenceNumber'
        },
        {
          title: 'SUPPLIER PO NUMBER',
          key: 'supplierOrderNumber',
          text: 'SUPPLIER PO NUMBER',
          value: 'supplierOrderNumber'
        },
        {
          title: 'ORDER NUMBER',
          key: 'storeOrderReference',
          text: 'ORDER NUMBER',
          value: 'storeOrderReference'
        },
        {
          title: 'TRADING TERMS',
          key: 'tenantOwnerSupplierChargeIncTax',
          text: 'TRADING TERMS',
          value: 'tenantOwnerSupplierChargeIncTax',
          width: '125px'
        },
        {
          title: 'FINAL TOTAL',
          key: 'supplierPayoutTotalIncTax',
          text: 'FINAL TOTAL',
          value: 'supplierPayoutTotalIncTax',
          width: '125px'
        },
        {
          title: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          key: 'store.name',
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          value: 'store.name'
        },
        {
          title: 'PAYMENT TYPE',
          key: 'paymentType',
          text: 'PAYMENT TYPE',
          value: 'paymentType',
          width: '110px'
        },
        {
          title: 'FINALISED',
          key: 'finalisedDate',
          text: 'FINALISED',
          value: 'finalisedDate'
        },
        {
          title: 'PAYMENT TRANSACTION PERIOD',
          key: 'transactionPeriod',
          text: 'PAYMENT TRANSACTION PERIOD',
          value: 'transactionPeriod'
        },
        {
          title: 'PAYOUT DATE',
          key: 'supplierPayoutDate',
          text: 'PAYOUT DATE',
          value: 'supplierPayoutDate'
        }
      ]
    },

    noSearchCriteria() {
      return !(
        this.queryObject.searchText ||
        this.queryObject.startDate ||
        this.queryObject.endDate
      )
    },

    transactionsGrandTotal() {
      return this.transactions.reduce(
        (acc, transaction) =>
          parseFloat((acc + transaction.supplierPayoutTotalIncTax).toFixed(2)),
        0
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification', 'removeNotification']),

    replacePaymentPeriodHeader() {
      replacePaymentPeriodHeader(
        this.headers,
        this.tenantPaymentTransactionsConfig
      )
    },

    fetch(query) {
      this.neverFetched = false
      this.fetching = true

      fetchTransactions(query, this.selectedPaymentType)
        .then((response) => {
          this.transactions = response.map((transaction) => ({
            ...transaction,
            payoutDisplayDate:
              transaction.stripePayoutDate ||
              transaction.supplierPayoutDate ||
              null,
            payoutDisplayStatus: transaction.stripePayoutDate
              ? 'Paid'
              : transaction.supplierPayoutDate
                ? 'Expected'
                : null
          }))
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetching = false
        })
    },

    searchQuery(queryObject) {
      this.queryObject = queryObject
      if (this.noSearchCriteria) {
        this.confirmationDialog = true
      } else {
        this.doSearch()
      }
    },

    doSearch() {
      this.confirmationDialog = false
      this.transactions = []
      this.fetch(this.queryObject.query)
    },

    selectPaymentType(paymentType) {
      if (!paymentType || paymentType === this.selectedPaymentType) return
      this.selectedPaymentType = paymentType
      this.transactions = []
      this.fetch(this.queryObject.query)
    },

    paymentTransactionsRouteTo(transaction) {
      const status = moment().isAfter(
        moment(transaction.transactionPeriod.endDate)
      )
        ? 'PAST'
        : 'UPCOMING'
      return {
        name:
          transaction.paymentType === 'CREDIT_CARD'
            ? 'card-transactions'
            : 'payment-transactions',
        query: { reference: transaction.referenceNumber, status }
      }
    },

    createLinkForTransaction(transaction) {
      const objectId = transaction.objectId.toString()
      switch (transaction.type) {
        case 'SUPPLIER_ORDER':
          return {
            name: 'purchase-order-details',
            params: { id: objectId }
          }

        case 'SUPPLIER_ORDER_QUERY':
          return {
            name: 'order-query-details',
            params: { id: objectId }
          }

        case 'CREDIT_RETURN':
          return {
            name: 'credit-return-details',
            params: { id: objectId }
          }

        case 'PAYMENT_ADJUSTMENT':
          return {
            name: 'payment-adjustment-details',
            params: { paymentAdjustmentId: objectId }
          }
      }
    },

    mapToDisplayType(type) {
      switch (type) {
        case 'SUPPLIER_ORDER':
          return 'Supplier PO'

        case 'SUPPLIER_ORDER_QUERY':
          return 'Order Query'

        case 'CREDIT_RETURN':
          return 'Credit Return'

        case 'PAYMENT_ADJUSTMENT':
          return 'Adjustment'
      }
    },

    mapToDisplayStatus(status) {
      switch (status) {
        case 'FINALISED':
          return 'Finalised'

        case 'IN_PROCESS':
          return 'In Process'

        case 'APPROVED':
          return 'Approved'

        case 'REJECTED':
          return 'Rejected'

        case 'CANCELLED':
          return 'Cancelled'

        case 'PROCESSED_OFFLINE':
          return 'Processed Offline'

        case 'OUTSTANDING':
          return 'Outstanding'
      }
    },

    fetchExcel(queryObject) {
      this.fetchingExcel = true
      this.addNotification({
        color: 'secondary',
        text: `Exporting data can take a while, please wait...`,
        timeout: -1
      })

      fetchTransactionsExcel(queryObject, this.selectedPaymentType)
        .then((response) => {
          this.removeNotification()
          this.generateFileDownload({
            response
          })
        })
        .catch((error) => {
          this.notifyDownloadError(error)
        })
        .finally(() => {
          this.fetchingExcel = false
        })
    },

    generateFileDownload(payload) {
      const { response } = payload

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const downloadDate = moment().format(
        COMMON_CONSTANTS.DATE_FORMAT_FOR_DOWNLOAD_FILE_NAME
      )

      const link = document.createElement('a')

      link.href = window.URL.createObjectURL(blob)
      link.download = `transactions_${downloadDate}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    notifyDownloadError(error) {
      error.response.status === 404
        ? this.addNotification({
            color: 'error',
            text: `Results could not be exported`
          })
        : this.addNotification({
            color: 'error',
            defaultError: true
          })
    }
  },

  mounted() {
    if (!this.canAccessPaymentServices && this.canAccessCardServices) {
      this.selectedPaymentType = 'CREDIT_CARD'
    }
    this.replacePaymentPeriodHeader()
  }
}
</script>

<style lang="scss" scoped>
#financial-transactions {
  .highlight-status-text-color {
    color: red !important;
  }

  .p-btn-pdf {
    color: $link-color;

    &:before {
      font-size: 32px;
    }
  }

  tr:last-child td {
    background-color: var(--v-lightHighlightBackground-base) !important;
    border-top: 2px solid var(--v-primary-base);
    font-weight: bold;
  }
}

@media only screen and (min-width: 1264px) {
  #financial-transactions {
    .p-btn {
      width: 125px;
    }
  }
}
</style>
